import type {
  LoaderFunction,
  MetaArgs,
  MetaDescriptor,
  MetaFunction,
} from '@remix-run/node';

import type { RootLoader } from '~/root';

/**
 * Merges metadata from parent routes with the provided overrides.
 * Ensures each route has the default tags and links from the root meta function.
 *
 * More info: https://remix.run/docs/en/main/route/meta#gotchas-with-meta-and-nested-routes
 */
export const mergeMeta = <
  Loader extends LoaderFunction | unknown = unknown,
  ParentsLoaders extends Record<string, LoaderFunction | unknown> = {
    root: RootLoader;
  },
>(
  overrideFn: MetaFunction<Loader, ParentsLoaders>,
): MetaFunction<Loader, ParentsLoaders> => {
  return (arg) => {
    // get meta from parent routes
    const mergedMeta = arg.matches.flatMap((match) => match.meta ?? []);

    // replace any parent meta with the same name or property with the override
    const overrides = overrideFn(arg);
    for (const override of overrides) {
      const index = mergedMeta.findIndex(
        (meta) =>
          ('name' in meta &&
            'name' in override &&
            meta.name === override.name) ||
          ('property' in meta &&
            'property' in override &&
            meta.property === override.property) ||
          ('title' in meta && 'title' in override),
      );

      if (index !== -1) {
        mergedMeta.splice(index, 1, override);
      } else {
        mergedMeta.push(override);
      }
    }

    return mergedMeta;
  };
};

export const getStaticPageTitle = ({
  title,
  matches,
}: {
  title: string;
  matches: MetaArgs<
    unknown,
    {
      root: RootLoader;
    }
  >['matches'];
}): MetaDescriptor => {
  const config = matches.find((match) => match.id === 'root')?.data.config;

  return { title: `${title} | ${config?.BRAND_NAME}` };
};
